import { useCallback, useEffect, useState } from 'react';
import {
  type AudioDeviceOption,
  listAudioDevices,
  rememberMicrophoneId,
  rememberSpeakerId,
  resolveDeviceId,
  storedMicrophoneId,
  storedSpeakerId,
} from './audioDevices.ts';
import { type MicrophoneBlock, microphoneBlockOf } from './microphoneBlock.ts';
import { queryMicrophonePermission } from './queryMicrophonePermission.ts';

/** How much a reading has to move before the screen is asked to redraw. */
const LEVEL_STEP = 0.02;
/** The bins that carry a voice; the ones above are hiss. */
const VOICE_BINS = 32;

export interface AudioSetup {
  microphones: AudioDeviceOption[];
  speakers: AudioDeviceOption[];
  /** Null while nothing has been listed, or when nothing is plugged in. */
  microphoneId: string | null;
  speakerId: string | null;
  /** How loud the chosen microphone is hearing the room, `0`–`1`. */
  level: number;
  /** Null while the microphone is not refused; otherwise who refused it. */
  block: MicrophoneBlock | null;
  chooseMicrophone: (deviceId: string) => void;
  chooseSpeaker: (deviceId: string) => void;
  /** Ask for the microphone again, after the user changed a setting. */
  retry: () => void;
}

/**
 * Listen to a microphone without being in a call yet.
 *
 * Opened only while the screen is asking which device to use, and closed the
 * moment it stops: a page holding an open microphone lights the recording
 * indicator, which on this screen would say the practice has already started.
 */
function openLevelMeter(
  stream: MediaStream,
  onLevel: (level: number) => void,
): () => void {
  const context = new AudioContext();
  const analyser = context.createAnalyser();
  analyser.fftSize = 256;
  context.createMediaStreamSource(stream).connect(analyser);

  const bins = new Uint8Array(analyser.frequencyBinCount);
  let frame = requestAnimationFrame(function read() {
    analyser.getByteFrequencyData(bins);

    let sum = 0;
    for (let index = 0; index < VOICE_BINS; index += 1) {
      sum += bins[index] ?? 0;
    }

    onLevel(sum / (VOICE_BINS * 255));
    frame = requestAnimationFrame(read);
  });

  return () => {
    cancelAnimationFrame(frame);
    void context.close();
  };
}

/**
 * Which microphone and which speaker, decided before the conversation starts.
 *
 * The question is only worth asking while there is still time to answer it,
 * and the answer is only trustworthy if the bar moves while they say
 * something. What they choose is remembered, so the question mostly answers
 * itself next time.
 *
 * `active` is what opens and closes the microphone — it goes false the instant
 * the real call takes over, so the preview never competes with it for the
 * device.
 */
export function useAudioSetup(active: boolean): AudioSetup {
  const [microphones, setMicrophones] = useState<AudioDeviceOption[]>([]);
  const [speakers, setSpeakers] = useState<AudioDeviceOption[]>([]);
  const [microphoneId, setMicrophoneId] = useState<string | null>(storedMicrophoneId);
  const [speakerId, setSpeakerId] = useState<string | null>(storedSpeakerId);
  const [level, setLevel] = useState(0);
  const [block, setBlock] = useState<MicrophoneBlock | null>(null);
  const [attempt, setAttempt] = useState(0);

  /*
   * The device list is asked for again whenever the chosen microphone changes,
   * because the first ask happens before permission has been given and comes
   * back nameless — "Microphone 1, Microphone 2" is not a choice anyone can
   * make. The stream that grants the names is the same one the meter reads.
   */
  useEffect(() => {
    if (!active) {
      return;
    }

    let gone = false;
    let stream: MediaStream | null = null;
    let closeMeter: (() => void) | null = null;

    async function start(): Promise<void> {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: microphoneId === null ? true : { deviceId: { exact: microphoneId } },
        });
        /*
         * The cleanup below can only stop a stream that exists. When the mode
         * flips while the browser is still handing the microphone over, that
         * moment has passed — stop it here or it stays open for the whole
         * conversation, recording light on.
         */
        if (gone) {
          stream.getTracks().forEach((track) => track.stop());
          stream = null;
          return;
        }
        setBlock(null);
      } catch (caught) {
        /*
         * A refused microphone is not a dead end here: the pickers still list
         * what is there, and the screen says what to do about it.
         */
        const refusedBy = await microphoneBlockOf(caught);
        if (!gone) {
          setBlock(refusedBy);
        }
      }

      /*
       * A browser that will not enumerate its own devices — an old one, or one
       * behind a policy — leaves the pickers empty rather than the screen
       * broken: the conversation runs on whatever the default device is.
       */
      const devices = await listAudioDevices().catch(() => ({
        microphones: [],
        speakers: [],
      }));
      if (gone) {
        return;
      }

      setMicrophones(devices.microphones);
      setSpeakers(devices.speakers);
      setMicrophoneId((current) => resolveDeviceId(current, devices.microphones));
      setSpeakerId((current) => resolveDeviceId(current, devices.speakers));

      if (stream !== null) {
        closeMeter = openLevelMeter(stream, (next) =>
          setLevel((shown) => (Math.abs(next - shown) < LEVEL_STEP ? shown : next)),
        );
      }
    }

    /*
     * Nothing here is worth breaking the screen the conversation starts from.
     * A browser with no audio analyser leaves the bar at rest and the rest of
     * the card working.
     */
    void start().catch(() => setLevel(0));

    return () => {
      gone = true;
      closeMeter?.();
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [active, microphoneId, attempt]);

  const retry = useCallback(() => setAttempt((count) => count + 1), []);

  /*
   * Allowing the microphone in the browser's settings does not reach the page
   * on its own; without this the screen stays refused until a reload. Browsers
   * that never announce the change are covered by the retry button.
   */
  useEffect(() => {
    if (!active || block === null) {
      return;
    }

    let gone = false;
    let status: PermissionStatus | null = null;
    const onChange = () => {
      if (status?.state !== 'denied') {
        retry();
      }
    };

    void queryMicrophonePermission().then((found) => {
      if (gone || found === null) {
        return;
      }
      status = found;
      status.addEventListener('change', onChange);
    });

    return () => {
      gone = true;
      status?.removeEventListener('change', onChange);
    };
  }, [active, block, retry]);

  const chooseMicrophone = useCallback((deviceId: string) => {
    rememberMicrophoneId(deviceId);
    setMicrophoneId(deviceId);
  }, []);

  const chooseSpeaker = useCallback((deviceId: string) => {
    rememberSpeakerId(deviceId);
    setSpeakerId(deviceId);
  }, []);

  return {
    microphones,
    speakers,
    microphoneId,
    speakerId,
    /*
     * A meter that is not listening reads zero rather than whatever the room
     * was doing when it stopped — the last frame of a closed microphone is a
     * bar that says somebody is still being heard.
     */
    level: active ? level : 0,
    block,
    chooseMicrophone,
    chooseSpeaker,
    retry,
  };
}
